import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { CameraCapture } from '@/types';
import { CAMERA_CONFIG } from '@/lib/constants';
import { useAppConfig } from './AppConfigContext';

interface CameraContextValue {
  isEnabled: boolean;
  isStreaming: boolean;
  stream: MediaStream | null;
  enable: () => Promise<void>;
  disable: () => void;
  captureFrame: () => Promise<CameraCapture | null>;
}

const CameraContext = createContext<CameraContextValue | undefined>(undefined);

export function CameraProvider({ children }: { children: React.ReactNode }) {
  const { config } = useAppConfig();
  const [isStreaming, setIsStreaming] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const isEnabled = config.cameraEnabled;

  // Enable camera stream
  const enable = useCallback(async () => {
    if (isStreaming) {
      console.warn('Camera already streaming');
      return;
    }

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia(
        CAMERA_CONFIG.videoConstraints
      );

      setStream(mediaStream);
      setIsStreaming(true);

      // Create video element for capturing frames
      if (!videoRef.current) {
        videoRef.current = document.createElement('video');
        videoRef.current.autoplay = true;
        videoRef.current.playsInline = true;
      }

      videoRef.current.srcObject = mediaStream;

      // Wait for video to be ready
      await new Promise<void>((resolve) => {
        if (videoRef.current) {
          videoRef.current.onloadedmetadata = () => {
            videoRef.current?.play();
            resolve();
          };
        }
      });

      console.log('📷 Camera enabled');
    } catch (error) {
      console.error('Failed to enable camera:', error);
      setIsStreaming(false);
      throw error;
    }
  }, [isStreaming]);

  // Disable camera stream
  const disable = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setIsStreaming(false);
    console.log('📷 Camera disabled');
  }, [stream]);

  // Capture a single frame from video
  const captureFrame = useCallback(async (): Promise<CameraCapture | null> => {
    if (!isStreaming || !videoRef.current) {
      console.warn('Camera not streaming, cannot capture frame');
      return null;
    }

    try {
      const video = videoRef.current;

      // Create canvas if not exists
      if (!canvasRef.current) {
        canvasRef.current = document.createElement('canvas');
      }

      const canvas = canvasRef.current;
      // Use optimized capture dimensions (smaller than video stream for bandwidth savings)
      canvas.width = CAMERA_CONFIG.captureWidth;
      canvas.height = CAMERA_CONFIG.captureHeight;

      // Draw current video frame to canvas with resize
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }

      // Draw and resize: source (video) -> destination (canvas with smaller dimensions)
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert to data URL
      const dataUrl = canvas.toDataURL(
        CAMERA_CONFIG.captureFormat,
        CAMERA_CONFIG.captureQuality
      );

      const capture: CameraCapture = {
        dataUrl,
        width: canvas.width,
        height: canvas.height,
        timestamp: Date.now(),
      };

      // Calculate and log file size (base64 data URL size in KB)
      const base64Size = Math.round((dataUrl.length * 3) / 4 / 1024);
      console.log('📸 Frame captured:', {
        resolution: `${capture.width}x${capture.height}`,
        quality: CAMERA_CONFIG.captureQuality,
        sizeKB: base64Size,
        videoSize: `${video.videoWidth}x${video.videoHeight}`
      });
      return capture;
    } catch (error) {
      console.error('Failed to capture frame:', error);
      return null;
    }
  }, [isStreaming]);

  // Auto-enable camera if configured
  useEffect(() => {
    if (isEnabled && !isStreaming) {
      console.log('📷 Auto-enabling camera (config changed to enabled)');
      enable().catch(error => {
        console.error('Failed to auto-enable camera:', error);
      });
    } else if (!isEnabled && isStreaming) {
      console.log('📷 Auto-disabling camera (config changed to disabled)');
      disable();
    }

    return () => {
      // Cleanup on unmount
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [isEnabled]); // Only depend on isEnabled, not isStreaming or enable

  const value: CameraContextValue = {
    isEnabled,
    isStreaming,
    stream,
    enable,
    disable,
    captureFrame,
  };

  return (
    <CameraContext.Provider value={value}>
      {children}
    </CameraContext.Provider>
  );
}

export function useCamera() {
  const context = useContext(CameraContext);
  if (context === undefined) {
    throw new Error('useCamera must be used within CameraProvider');
  }
  return context;
}
